import { useCallback, useEffect, useState } from "react";
import {
  ExternalLink, Loader2, ChevronDown, ChevronRight, AlertCircle, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { adminApi } from "../../lib/adminAuth";
import { fdaUseLabel } from "../../lib/sources";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short" });
  } catch { return ""; }
}

function SectionRow({ sec, open, onToggle, onUse }) {
  return (
    <div className="border-t border-stone-100 py-2" data-testid={`label-section-${sec.n}`}>
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={onToggle}
          className="flex min-w-0 items-center gap-1 text-left text-[13px] font-medium text-stone-800 hover:text-stone-950"
          data-testid={`label-section-toggle-${sec.n}`}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
          <a
            href={sec.url} target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="truncate hover:underline"
            data-testid={`label-section-link-${sec.n}`}
          >
            §{sec.n} {sec.label}
          </a>
        </button>
        <Button
          size="sm" variant="outline" className="h-6 shrink-0 px-2 text-[11px]"
          onClick={() => onUse(sec)} data-testid={`label-use-${sec.n}`}
        >
          Use
        </Button>
      </div>
      {open && (
        <p
          className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-stone-50 p-2 text-[12px] leading-relaxed text-stone-600"
          data-testid={`label-excerpt-${sec.n}`}
        >
          {sec.text}{sec.truncated ? " …" : ""}
        </p>
      )}
    </div>
  );
}

/**
 * Read-only FDA label panel (DailyMed). Shows the matched label's real section
 * text; "Use" calls onUse({ url, section }) so the parent can cite it.
 */
export default function FdaLabelPanel({ medicationId, medicationName, onUse, useHint }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openN, setOpenN] = useState(null);
  const [showPaste, setShowPaste] = useState(false);
  const [setid, setSetid] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (!medicationId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: d } = await adminApi().get(
        `/admin/dailymed/for-medication/${medicationId}${refresh ? "?refresh=true" : ""}`,
      );
      setData(d);
      const first = d?.label?.sections?.[0]?.n;
      setOpenN(first || null);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Couldn't load the FDA label." });
    } finally { setLoading(false); }
  }, [medicationId]);

  useEffect(() => { load(); }, [load]);

  async function applySetid() {
    if (setid.trim().length < 20) { toast.error("Paste a valid DailyMed setid."); return; }
    setBusy(true);
    try {
      const { data: d } = await adminApi().post(
        `/admin/dailymed/for-medication/${medicationId}`, { setid: setid.trim() },
      );
      setData(d);
      setOpenN(d?.label?.sections?.[0]?.n || null);
      setShowPaste(false);
      setSetid("");
      toast.success("Label updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load that setid");
    } finally { setBusy(false); }
  }

  function useSection(sec) {
    const added = onUse?.({ url: sec.url, label: fdaUseLabel(sec) });
    if (added === false) toast.message("Already added");
    else toast.success(`Added §${sec.n} ${sec.label}`);
  }

  const label = data?.label;
  const searchUrl = `https://dailymed.nlm.nih.gov/dailymed/search.cfm?query=${encodeURIComponent(medicationName || "")}`;

  return (
    <div
      data-testid="fda-label-panel"
      className="rounded-xl border border-stone-200 bg-white p-4 text-sm"
    >
      <div className="flex items-center gap-1.5 text-stone-800">
        <FileText className="h-4 w-4 text-stone-500" />
        <span className="font-semibold">FDA label · {medicationName}</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-stone-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Fetching from DailyMed…
        </div>
      ) : (
        <>
          {label ? (
            <>
              <p className="mt-1 text-[12px] text-stone-500">
                DailyMed, cached {fmtDate(label.cached_at)}
              </p>
              <div className="mt-2 rounded-lg bg-stone-50 px-3 py-2">
                <p className="text-[13px] font-medium text-stone-800" data-testid="label-matched-title">
                  {label.display_title || label.title}
                </p>
                {label.manufacturer && (
                  <p className="text-[12px] text-stone-500" data-testid="label-manufacturer">
                    {label.manufacturer}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                  <a href={label.source_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-800">
                    View on DailyMed <ExternalLink className="h-3 w-3" />
                  </a>
                  <button onClick={() => setShowPaste((s) => !s)}
                     className="text-stone-500 underline-offset-2 hover:text-stone-800 hover:underline"
                     data-testid="label-wrong-link">
                    Wrong label? paste setid
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
                 data-testid="label-not-found">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>
                {data?.error || "No label loaded."}{" "}
                <a href={searchUrl} target="_blank" rel="noreferrer" className="font-medium underline">
                  Find label on DailyMed
                </a>, then paste its setid below.
              </div>
            </div>
          )}

          {(showPaste || !label) && (
            <div className="mt-2 flex items-center gap-2" data-testid="label-paste-row">
              <Input
                value={setid} onChange={(e) => setSetid(e.target.value)}
                placeholder="Paste DailyMed setid (UUID)"
                className="h-8 text-xs" data-testid="label-setid-input"
              />
              <Button size="sm" className="h-8" onClick={applySetid} disabled={busy}
                      data-testid="label-setid-apply">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}
              </Button>
            </div>
          )}

          {useHint && label && (
            <p className="mt-3 text-[11px] text-emerald-700" data-testid="label-use-hint">
              Use adds a source to: {useHint}
            </p>
          )}

          {label?.sections?.length > 0 && (
            <div className="mt-2">
              {label.sections.map((sec) => (
                <SectionRow
                  key={sec.n} sec={sec} open={openN === sec.n}
                  onToggle={() => setOpenN(openN === sec.n ? null : sec.n)}
                  onUse={useSection}
                />
              ))}
            </div>
          )}

          <div className="mt-3 border-t border-stone-100 pt-2">
            <p className="text-[12px] font-semibold text-stone-700">Sources already used</p>
            {data?.sources_used?.length > 0 ? (
              <p className="mt-1 text-[12px] leading-relaxed">
                {data.sources_used.map((s, i) => (
                  <span key={s.url}>
                    {i > 0 && <span className="text-stone-400"> · </span>}
                    <a href={s.url} target="_blank" rel="noreferrer"
                       className="text-stone-600 hover:underline">{s.label}</a>
                  </span>
                ))}
              </p>
            ) : (
              <p className="mt-1 text-[12px] text-stone-400">
                None yet — verified interaction citations will appear here.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
